import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AdminActions } from "src/redux/Admin/actions";
import TermsPublisher, { DOCUMENTS as TERMS_DOCUMENTS, readFile } from "./TermsPublisher";
import { ConfirmActions } from "src/redux/Confirm/actions";
import useUnsavedChangesGuard from "src/utils/useUnsavedChangesGuard";
import Button from "src/components/common/Button";
import CheckBox from "src/components/common/CheckBox";
import MarketDataProviders, { PROVIDERS } from "./MarketDataProviders";
import MarketDataSchedule, { ruleKey, defaultDraft, functionLabel } from "./MarketDataSchedule";
import CommandSchedules, { defaultSchedule, commandLabel } from "./CommandSchedules";
import styles from "./styles.module.scss";

// Random shared HFS key: 128 random bits. The operator can overwrite it with
// an existing customer's key before saving so their integration keeps working.
const generateApiKey = () => {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    const hex = Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
    return `hfs-${hex}`;
};

// A valid starting draft (function/provider/market constrained by FUNCTION_SPEC).
const emptyDraft = defaultDraft;

const normalizeRule = (r) =>
    JSON.stringify({
        function: r.function,
        provider: r.provider,
        market: r.market || "",
        runTimes: [...(r.runTimes || [])].sort(),
        timezone: r.timezone || "Asia/Seoul",
        enabled: !!r.enabled,
    });

// Canonical form of one command schedule, for dirty comparison. daysOfWeek is
// left in its stored order (the UI already keeps it MON..SUN); nthWeekday only
// matters for the monthly cadence but is compared verbatim - harmless.
const normalizeCommandSchedule = (s) =>
    JSON.stringify({
        enabled: !!s.enabled,
        time: s.time || "",
        timezone: s.timezone || "Asia/Seoul",
        cadence: s.cadence || "daily",
        daysOfWeek: s.daysOfWeek || [],
        nthWeekday: s.nthWeekday || 1,
    });

/**
 * Platform Settings: one page, one confirm/discard bar. HFS, market-data
 * providers, and the import schedule all stage edits into local pending state;
 * nothing hits the server until "Confirm Changes" (behind the standard reason
 * dialog), and navigating away with unsaved edits is guarded like every other
 * admin page. There is no per-row save.
 */
const PlatformSettings = () => {
    const dispatch = useDispatch();
    const adminServiceUrl = useSelector((s) => s.Auth.adminServiceUrl);
    const [stored, setStored] = useState(null); // server truth (see load())
    const [loading, setLoading] = useState(true);
    const [msg, setMsg] = useState(null);
    const [saving, setSaving] = useState(false);
    const [exporting, setExporting] = useState(false);
    const nextKeyRef = useRef(1);

    // ---- pending edits ----
    const [hfsEnabled, setHfsEnabled] = useState(false);
    const [hfsKeyEditing, setHfsKeyEditing] = useState(false);
    const [hfsKeyInput, setHfsKeyInput] = useState("");
    const [hfsKeyStaged, setHfsKeyStaged] = useState(null); // null = unchanged

    const [offerExpirySecs, setOfferExpirySecs] = useState(""); // string (number input)
    const [dailyDeletionHourKst, setDailyDeletionHourKst] = useState(0); // int, 0-23 KST

    const [provEnabled, setProvEnabled] = useState({}); // id -> bool
    const [provSecrets, setProvSecrets] = useState({}); // id -> { field: value }
    const [provReplacing, setProvReplacing] = useState({}); // id -> { field: true }

    const [rules, setRules] = useState([]); // working copy; new rules carry __new + __key
    const [deletedRuleIds, setDeletedRuleIds] = useState([]);
    const [draft, setDraft] = useState(emptyDraft());
    // Legal documents chosen but not yet published. Staged like every other
    // edit on this page: the bottom bar reports them, Confirm Changes applies
    // them, and Discard drops them.
    const [termsFiles, setTermsFiles] = useState({});
    const [termsEffectiveDate, setTermsEffectiveDate] = useState("");
    // Bumped after a save so the terms section re-reads what is published:
    // publishing happens here, and the section would otherwise keep showing
    // the version it loaded when it mounted.
    const [termsReloads, setTermsReloads] = useState(0);

    // Recurring platform-command schedules (issue #31): a fixed command list from
    // the server + a working copy of the per-command config keyed by command name.
    const [cmdSchedules, setCmdSchedules] = useState({}); // command -> schedule

    // Reset all pending edits to a stored snapshot. Purely local - no fetch, no
    // loading flip - so Discard keeps the current scroll position.
    const resetPending = useCallback((s) => {
        setHfsEnabled(!!s.hfsEnabled);
        setHfsKeyEditing(false);
        setHfsKeyInput("");
        setHfsKeyStaged(null);
        setOfferExpirySecs(String(s.offerExpirySecs));
        setDailyDeletionHourKst(s.dailyDeletionHourKst);
        const enabled = {};
        PROVIDERS.forEach((prov) => {
            enabled[prov.id] = !!s.providers[prov.id]?.enabled;
        });
        setProvEnabled(enabled);
        setProvSecrets({});
        setProvReplacing({});
        setRules(
            (s.schedule || []).map((r) => ({
                id: r.id,
                function: r.function,
                provider: r.provider,
                market: r.market || "",
                runTimes: r.runTimes || [],
                timezone: r.timezone || "Asia/Seoul",
                enabled: !!r.enabled,
            }))
        );
        setDeletedRuleIds([]);
        setDraft(emptyDraft());
        // Seed each schedulable command with its stored config, falling back to a
        // disabled default so a never-configured command still renders a row.
        const cmds = {};
        (s.commands || []).forEach((cmd) => {
            cmds[cmd] = { ...defaultSchedule(), ...(s.commandSchedules?.[cmd] || {}) };
        });
        setCmdSchedules(cmds);
    }, []);

    // Fetch server truth + reset pending, without touching the loading flag, so
    // a post-save refresh keeps the current scroll position.
    const refresh = useCallback(async () => {
        const [settings, provRes, schedRes, cmdRes] = await Promise.all([
            dispatch(AdminActions.FetchPlatformSettings()),
            dispatch(AdminActions.FetchMarketDataProviders()),
            dispatch(AdminActions.FetchMarketDataSchedule()),
            dispatch(AdminActions.FetchCommandSchedules()),
        ]);
        const provById = {};
        (provRes?.providers || []).forEach((p) => {
            provById[p.provider] = p;
        });
        const storedObj = {
            hfsEnabled: !!settings.hfsEnabled,
            hfsKeyConfigured: !!settings.hfsKeyConfigured,
            offerExpirySecs: settings.offerExpirySecs,
            dailyDeletionHourKst: settings.dailyDeletionHourKst,
            providers: provById,
            schedule: schedRes?.rules || [],
            scheduleStatuses: schedRes?.statuses || {},
            commands: cmdRes?.commands || [],
            commandSchedules: cmdRes?.schedules || {},
            commandStatuses: cmdRes?.statuses || {},
        };
        setStored(storedObj);
        resetPending(storedObj);
    }, [dispatch, resetPending]);

    // Initial page load shows the loading screen; later refreshes use refresh().
    const load = useCallback(async () => {
        setLoading(true);
        try {
            await refresh();
        } catch (e) {
            setMsg("Failed to load platform settings: " + e.message);
        } finally {
            setLoading(false);
        }
    }, [refresh]);

    useEffect(() => {
        load();
    }, [load]);

    // ---- provider handlers ----
    const onToggleEnabled = (id) => setProvEnabled((p) => ({ ...p, [id]: !p[id] }));
    const onSecretChange = (id, field, val) =>
        setProvSecrets((p) => ({ ...p, [id]: { ...(p[id] || {}), [field]: val } }));
    const onStartReplace = (id, field) => {
        setProvReplacing((p) => ({ ...p, [id]: { ...(p[id] || {}), [field]: true } }));
        onSecretChange(id, field, "");
    };
    const onCancelReplace = (id, field) => {
        setProvReplacing((p) => {
            const next = { ...(p[id] || {}) };
            delete next[field];
            return { ...p, [id]: next };
        });
        setProvSecrets((p) => {
            const next = { ...(p[id] || {}) };
            delete next[field];
            return { ...p, [id]: next };
        });
    };

    // ---- schedule handlers ----
    const onEditRule = (key, patch) =>
        setRules((rs) => rs.map((r) => (ruleKey(r) === key ? { ...r, ...patch } : r)));
    const onDeleteRule = (r) => {
        setRules((rs) => rs.filter((x) => ruleKey(x) !== ruleKey(r)));
        if (r.id) setDeletedRuleIds((ids) => [...ids, r.id]);
    };
    const onAddRule = () => {
        const key = "new-" + nextKeyRef.current++;
        // draft.market is already constrained to a valid value for the chosen
        // function/provider (or "" when that combination takes no market).
        setRules((rs) => [
            ...rs,
            {
                __new: true,
                __key: key,
                function: draft.function,
                provider: draft.provider,
                market: draft.market || "",
                runTimes: draft.runTimes || [],
                timezone: draft.timezone || "Asia/Seoul",
                enabled: true,
            },
        ]);
        setDraft(emptyDraft());
    };
    const onDraftChange = (patch) => setDraft((d) => ({ ...d, ...patch }));
    const onViewHistory = useCallback(
        (id) => dispatch(AdminActions.FetchMarketDataScheduleHistory(id)),
        [dispatch]
    );

    // ---- command-schedule handlers ----
    const onEditCommand = (cmd, patch) =>
        setCmdSchedules((cs) => ({ ...cs, [cmd]: { ...(cs[cmd] || defaultSchedule()), ...patch } }));
    const onViewCommandHistory = useCallback(
        (cmd) => dispatch(AdminActions.FetchCommandScheduleHistory(cmd)),
        [dispatch]
    );
    // Refetch only the per-command status badges, leaving pending edits untouched.
    const refreshCommandStatuses = useCallback(async () => {
        try {
            const res = await dispatch(AdminActions.FetchCommandSchedules());
            setStored((s) => (s ? { ...s, commandStatuses: res?.statuses || {} } : s));
        } catch (e) {
            console.error("Failed to refresh command statuses", e);
        }
    }, [dispatch]);
    // Run now: confirm (required reason, audited) then trigger the command; the
    // scheduler fires it immediately and records a run in history.
    const onRunCommandNow = useCallback(
        (cmd) => {
            dispatch(ConfirmActions.Open({
                title: "Run now",
                message: `Run ${commandLabel(cmd)} now?`,
                getDescription: true,
                isDescriptionRequired: true,
                descriptionPlaceholder: "Reason for running this now",
                okTitle: "Run now",
                cancelTitle: "Cancel",
                onOk: async (reason) => {
                    try {
                        await dispatch(AdminActions.RunCommandScheduleNow(cmd, reason));
                        setMsg(`Triggered ${commandLabel(cmd)}. Watch View History for the result.`);
                        setTimeout(refreshCommandStatuses, 800);
                    } catch (e) {
                        setMsg("Failed to run: " + e.message);
                    }
                },
            }));
        },
        [dispatch, refreshCommandStatuses]
    );
    // Refetch only the per-rule status badges, leaving pending edits untouched.
    const refreshStatuses = useCallback(async () => {
        try {
            const schedRes = await dispatch(AdminActions.FetchMarketDataSchedule());
            setStored((s) => (s ? { ...s, scheduleStatuses: schedRes?.statuses || {} } : s));
        } catch (e) {
            console.error("Failed to refresh schedule statuses", e);
        }
    }, [dispatch]);
    // Run now: confirm (with a required reason, audited) then trigger the task.
    const onRunNow = useCallback(
        (rule) => {
            dispatch(ConfirmActions.Open({
                title: "Run now",
                message: `Run ${functionLabel(rule.function)} (${rule.provider}`
                    + `${rule.market ? " / " + rule.market : ""}) now?`,
                getDescription: true,
                isDescriptionRequired: true,
                descriptionPlaceholder: "Reason for running this now",
                okTitle: "Run now",
                cancelTitle: "Cancel",
                onOk: async (reason) => {
                    try {
                        await dispatch(AdminActions.RunTask({
                            id: rule.id,
                            function: rule.function,
                            provider: rule.provider,
                            market: rule.market || null,
                        }, reason));
                        setMsg(`Triggered ${functionLabel(rule.function)} (${rule.provider}). `
                            + "Watch View History for the result.");
                        // The scheduler records a running row within ~100ms;
                        // refetch the badges shortly after so the rule flips to
                        // Running (then to the outcome on a later refresh).
                        setTimeout(refreshStatuses, 800);
                    } catch (e) {
                        setMsg("Failed to run: " + e.message);
                    }
                },
            }));
        },
        [dispatch, refreshStatuses]
    );

    // ---- dirty detection + change summary ----
    const changes = [];
    if (stored) {
        if (hfsEnabled !== stored.hfsEnabled) changes.push(`HFS API ${hfsEnabled ? "enabled" : "disabled"}`);
        if (hfsKeyStaged != null) changes.push("HFS API key replaced");
        if (String(offerExpirySecs) !== String(stored.offerExpirySecs)) {
            changes.push(`Offer expiry → ${offerExpirySecs}s`);
        }
        if (dailyDeletionHourKst !== stored.dailyDeletionHourKst) {
            changes.push(`Daily deletion hour → ${String(dailyDeletionHourKst).padStart(2, "0")}:00 KST`);
        }
        PROVIDERS.forEach((prov) => {
            if (provEnabled[prov.id] !== !!stored.providers[prov.id]?.enabled) {
                changes.push(`${prov.label} ${provEnabled[prov.id] ? "enabled" : "disabled"}`);
            }
            const fields = Object.entries(provSecrets[prov.id] || {})
                .filter(([, v]) => v)
                .map(([k]) => k);
            if (fields.length) changes.push(`${prov.label} secret(s): ${fields.join(", ")}`);
        });
        // All four documents are one change: a version is the unit that is
        // published and consented to, so a partial set is not publishable and
        // is not reported as pending.
        // A partial set is still a pending change, so the bar appears and the
        // selection can be discarded. It cannot be applied, which is what
        // termsIncomplete below disables Confirm for.
        const termsChosen = TERMS_DOCUMENTS.filter((d) => termsFiles[d.name]);
        if (termsChosen.length === TERMS_DOCUMENTS.length) {
            changes.push(
                "Publish new Terms of Use and Privacy Policy"
                + (termsEffectiveDate ? `, effective ${termsEffectiveDate}` : "")
            );
        } else if (termsChosen.length > 0) {
            changes.push(
                `Terms & Privacy: ${termsChosen.length} of ${TERMS_DOCUMENTS.length} documents chosen`
            );
        }

        deletedRuleIds.forEach((id) => changes.push(`Delete schedule rule ${id}`));
        const storedById = {};
        stored.schedule.forEach((r) => {
            storedById[r.id] = r;
        });
        rules.forEach((r) => {
            if (r.__new) {
                changes.push(`Add schedule ${r.function}·${r.provider}${r.market ? "·" + r.market : ""}`);
            } else if (!storedById[r.id] || normalizeRule(r) !== normalizeRule(storedById[r.id])) {
                changes.push(`Update schedule ${r.function}·${r.provider}${r.market ? "·" + r.market : ""}`);
            }
        });
        (stored.commands || []).forEach((cmd) => {
            const storedSched = { ...defaultSchedule(), ...(stored.commandSchedules?.[cmd] || {}) };
            const cur = cmdSchedules[cmd] || defaultSchedule();
            if (normalizeCommandSchedule(cur) !== normalizeCommandSchedule(storedSched)) {
                changes.push(`Command schedule: ${commandLabel(cmd)} (${cur.enabled ? "enabled" : "disabled"})`);
            }
        });
    }
    // Some documents chosen but not all four. A version is published as a set,
    // so Confirm stays out of reach until the set is complete.
    const termsChosen = TERMS_DOCUMENTS.filter((d) => termsFiles[d.name]).length;
    // A version is published as a set, and with the date it takes effect: the
    // consent dialog states that date and nothing else in the system knows it.
    const termsIncomplete =
        termsChosen > 0
        && (termsChosen < TERMS_DOCUMENTS.length || !termsEffectiveDate);

    const dirty = changes.length > 0;
    const summary = changes.map((c) => "• " + c).join("\n");
    useUnsavedChangesGuard(dirty, summary);

    const changedRules = () => {
        const storedById = {};
        (stored?.schedule || []).forEach((r) => {
            storedById[r.id] = r;
        });
        return rules.filter((r) => r.__new || !storedById[r.id] || normalizeRule(r) !== normalizeRule(storedById[r.id]));
    };

    const discard = () => {
        if (!dirty) return;
        resetPending(stored); // local reset only - preserves scroll position
        setTermsFiles({});
        setMsg(null);
    };

    const confirm = () => {
        if (!dirty) return;
        dispatch(
            ConfirmActions.Open({
                title: "Save Platform Settings",
                message: "The following changes will be applied:\n" + summary,
                getDescription: true,
                isDescriptionRequired: true,
                descriptionPlaceholder: "Reason for change",
                okTitle: "Save",
                cancelTitle: "Cancel",
                onOk: async (reason) => {
                    setSaving(true);
                    setMsg(null);
                    try {
                        // Publishing puts every signed-in user behind the
                        // consent gate, so it goes through the same reason and
                        // the same confirmation as any other change here. All
                        // four documents or none: a version cannot be half
                        // published.
                        const termsReady =
                            TERMS_DOCUMENTS.every((d) => termsFiles[d.name]);
                        if (termsReady) {
                            const documents = {};
                            for (const doc of TERMS_DOCUMENTS) {
                                documents[doc.name] = await readFile(termsFiles[doc.name]);
                            }
                            documents.effectiveDate = termsEffectiveDate;
                            await dispatch(AdminActions.PublishTerms(documents, reason));
                            setTermsFiles({});
                            // The date stays: it is now the one in force, and
                            // the publisher shows it rather than a blank field.
                        }
                        if (hfsEnabled !== stored.hfsEnabled) {
                            await dispatch(AdminActions.SetPlatformHfsEnabled(hfsEnabled, reason));
                        }
                        if (hfsKeyStaged != null) {
                            await dispatch(AdminActions.SetPlatformHfsApiKey(hfsKeyStaged, reason));
                        }
                        if (String(offerExpirySecs) !== String(stored.offerExpirySecs)) {
                            const secs = Number(offerExpirySecs);
                            if (!Number.isInteger(secs) || secs <= 0) {
                                throw new Error("Offer expiry must be a positive whole number of seconds");
                            }
                            await dispatch(AdminActions.SetPlatformOfferExpiry(secs, reason));
                        }
                        if (dailyDeletionHourKst !== stored.dailyDeletionHourKst) {
                            await dispatch(AdminActions.SetPlatformDailyDeletionHour(dailyDeletionHourKst, reason));
                        }
                        for (const prov of PROVIDERS) {
                            const enabledChanged = provEnabled[prov.id] !== !!stored.providers[prov.id]?.enabled;
                            const secrets = {};
                            Object.entries(provSecrets[prov.id] || {}).forEach(([k, v]) => {
                                if (v) secrets[k] = v;
                            });
                            const hasSecrets = Object.keys(secrets).length > 0;
                            if (enabledChanged || hasSecrets) {
                                await dispatch(
                                    AdminActions.SetMarketDataProvider(prov.id, {
                                        enabled: provEnabled[prov.id],
                                        ...(hasSecrets ? { secrets } : {}),
                                    }, reason)
                                );
                            }
                        }
                        for (const id of deletedRuleIds) {
                            await dispatch(AdminActions.DeleteMarketDataScheduleRule(id, reason));
                        }
                        for (const r of changedRules()) {
                            await dispatch(
                                AdminActions.SaveMarketDataScheduleRule({
                                    ...(r.id ? { id: r.id } : {}),
                                    function: r.function,
                                    provider: r.provider,
                                    market: r.market || null,
                                    runTimes: r.runTimes || [],
                                    timezone: r.timezone || "Asia/Seoul",
                                    enabled: !!r.enabled,
                                }, reason)
                            );
                        }
                        // Command schedules are edited together and PUT wholesale
                        // (the endpoint replaces the whole config), so send them
                        // once if any command changed.
                        const commandsChanged = (stored.commands || []).some((cmd) => {
                            const storedSched = { ...defaultSchedule(), ...(stored.commandSchedules?.[cmd] || {}) };
                            const cur = cmdSchedules[cmd] || defaultSchedule();
                            return normalizeCommandSchedule(cur) !== normalizeCommandSchedule(storedSched);
                        });
                        if (commandsChanged) {
                            await dispatch(AdminActions.SetCommandSchedules(cmdSchedules, reason));
                        }
                        setMsg("Platform settings saved");
                        setTermsReloads((n) => n + 1);
                        await refresh(); // silent refresh - keeps scroll position
                    } catch (e) {
                        setMsg("Failed to save platform settings: " + e.message);
                    } finally {
                        setSaving(false);
                    }
                },
            })
        );
    };

    const startKeyEntry = () => {
        setHfsKeyInput(generateApiKey());
        setHfsKeyEditing(true);
    };
    const stageKey = () => {
        if (!hfsKeyInput) {
            setMsg("Enter or generate a key");
            return;
        }
        setHfsKeyStaged(hfsKeyInput);
        setHfsKeyEditing(false);
    };
    const cancelKeyEntry = () => {
        setHfsKeyEditing(false);
        setHfsKeyInput("");
    };
    const copyToClipboard = (text) => {
        if (navigator.clipboard) navigator.clipboard.writeText(text);
    };

    // Download all settings as a YubiKey-encrypted .age file. The response is a
    // binary blob (not JSON), so it bypasses fetchWrapper; the cookie session
    // authenticates, same as every other admin call.
    const exportSettings = async () => {
        // The export reads every cpty's config, relationships, and users, so it
        // can take a while on large platforms. Block re-clicks and tell the
        // operator it is running.
        setExporting(true);
        setMsg("Exporting all settings and encrypting to your YubiKey. This can take a while, please wait...");
        try {
            const resp = await fetch(adminServiceUrl + "/settings/export", { credentials: "include" });
            if (!resp.ok) {
                let detail = resp.status;
                try {
                    detail = (await resp.json()).error || detail;
                } catch (_) {
                    /* non-JSON error body */
                }
                setMsg("Export failed: " + detail);
                return;
            }
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "ksl-settings-export.age";
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            setMsg("Settings exported, encrypted to your YubiKey. Decrypt it with your YubiKey to read it.");
        } catch (e) {
            setMsg("Export failed: " + e.message);
        } finally {
            setExporting(false);
        }
    };

    if (loading) {
        return (
            <div className={styles.platform}>
                <div className={styles.loading}>Loading...</div>
            </div>
        );
    }

    return (
        <div className={`${styles.platform} ${dirty ? styles.hasStatusBar : ""}`}>
            <div className={styles.header}>
                <div className={styles.title}>Platform Settings</div>
            </div>

            {msg && <div className={styles.actionMsg}>{msg}</div>}

            <div className={styles.section}>
                <div className={styles.sectionTitle}>Export</div>
                <div className={styles.configForm}>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Export settings</span>
                        <div className={styles.apiKeyControl}>
                            <Button
                                value={exporting ? "Exporting, please wait..." : "Export (encrypted)"}
                                onClick={exportSettings}
                                disabled={exporting}
                                dataTestId="settings-export"
                            />
                            <span className={styles.configHint}>
                                Downloads all platform settings as a file encrypted to your YubiKey. Only your YubiKey can open it.
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.section}>
                <div className={styles.sectionTitle}>HFS API</div>
                <div className={styles.configForm}>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>HFS API Enabled</span>
                        <CheckBox
                            name="hfsEnabled"
                            selected={hfsEnabled}
                            onChange={() => setHfsEnabled((v) => !v)}
                            small
                            styleName="black"
                            dataTestId="platform-hfs-toggle"
                        >
                            <span className={styles.configHint}>
                                Global master switch for the shared HFS API. When off, all HFS traffic is refused regardless of key.
                            </span>
                        </CheckBox>
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Shared HFS API Key</span>
                        <div className={styles.apiKeyControl}>
                            {hfsKeyEditing ? (
                                <>
                                    {/* API key, not a login password: type=text so no password
                                        manager offers to fill/save it. */}
                                    <input
                                        type="text"
                                        value={hfsKeyInput}
                                        onChange={(e) => setHfsKeyInput(e.target.value)}
                                        className={styles.configInput}
                                        placeholder="Enter or generate a key"
                                        data-testid="hfs-api-key-input"
                                        autoComplete="off"
                                        spellCheck={false}
                                        data-lpignore="true"
                                        data-1p-ignore="true"
                                        data-bwignore="true"
                                        data-form-type="other"
                                    />
                                    <Button value="Copy" onClick={() => copyToClipboard(hfsKeyInput)} />
                                    <Button value="Regenerate" onClick={() => setHfsKeyInput(generateApiKey())} />
                                    <Button value="Stage key" onClick={stageKey} dataTestId="stage-hfs-api-key" />
                                    <Button value="Cancel" onClick={cancelKeyEntry} />
                                </>
                            ) : (
                                <>
                                    <span className={styles.configValue}>
                                        {hfsKeyStaged != null
                                            ? "New key staged (applies on Confirm)"
                                            : stored?.hfsKeyConfigured
                                            ? "Key configured"
                                            : "No key set"}
                                    </span>
                                    <Button
                                        value={stored?.hfsKeyConfigured || hfsKeyStaged != null ? "Replace key" : "Set key"}
                                        onClick={startKeyEntry}
                                        dataTestId="replace-hfs-api-key"
                                    />
                                    {hfsKeyStaged != null && (
                                        <Button value="Undo key" onClick={() => setHfsKeyStaged(null)} />
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}></span>
                        <span className={styles.configHint}>
                            For an existing HFS customer, paste their current key so it keeps working. It is stored as a one-way hash and cannot be shown again after saving.
                        </span>
                    </div>
                </div>
            </div>

            <div className={styles.section}>
                <div className={styles.sectionTitle}>Transaction Expiry</div>
                <div className={styles.configForm}>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Offer expiry (seconds)</span>
                        <div className={styles.apiKeyControl}>
                            <input
                                type="number"
                                min="1"
                                step="1"
                                value={offerExpirySecs}
                                onChange={(e) => setOfferExpirySecs(e.target.value)}
                                className={styles.configInput}
                                data-testid="platform-offer-expiry"
                            />
                            <span className={styles.configHint}>
                                How long an offer stays live before it expires. Read when an offer is created; existing offers keep their expiry.
                            </span>
                        </div>
                    </div>
                    <div className={styles.configRow}>
                        <span className={styles.configLabel}>Daily deletion time (KST)</span>
                        <div className={styles.apiKeyControl}>
                            <select
                                value={dailyDeletionHourKst}
                                onChange={(e) => setDailyDeletionHourKst(Number(e.target.value))}
                                className={styles.configSelect}
                                data-testid="platform-daily-deletion-hour"
                            >
                                {Array.from({ length: 24 }, (_, h) => (
                                    <option key={h} value={h}>
                                        {String(h).padStart(2, "0")}:00
                                    </option>
                                ))}
                            </select>
                            <span className={styles.configHint}>
                                Hour of day (Asia/Seoul), after trade hours, when stale negotiations from previous days are deleted.
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <MarketDataProviders
                stored={stored.providers}
                enabledById={provEnabled}
                secretsById={provSecrets}
                replacingById={provReplacing}
                onToggleEnabled={onToggleEnabled}
                onStartReplace={onStartReplace}
                onCancelReplace={onCancelReplace}
                onSecretChange={onSecretChange}
            />

            <MarketDataSchedule
                rules={rules}
                statuses={stored.scheduleStatuses || {}}
                onViewHistory={onViewHistory}
                onRunNow={onRunNow}
                onEditRule={onEditRule}
                onDeleteRule={onDeleteRule}
                draft={draft}
                onDraftChange={onDraftChange}
                onAddRule={onAddRule}
            />

            <CommandSchedules
                commands={stored.commands || []}
                schedules={cmdSchedules}
                statuses={stored.commandStatuses || {}}
                onEdit={onEditCommand}
                onViewHistory={onViewCommandHistory}
                onRunNow={onRunCommandNow}
            />

            <div className={styles.section}>
                <div className={styles.sectionTitle}>Terms &amp; Privacy</div>
                <TermsPublisher
                    reloadKey={termsReloads}
                    files={termsFiles}
                    onPick={(name, file) =>
                        setTermsFiles((prev) => ({ ...prev, [name]: file }))
                    }
                    effectiveDate={termsEffectiveDate}
                    onEffectiveDate={setTermsEffectiveDate}
                />
            </div>

            {dirty && (
                <div className={styles.statusBar}>
                    <span>
                        {termsIncomplete
                            ? "Terms & Privacy needs all four documents before changes can be saved"
                            : `${changes.length} unsaved change${changes.length > 1 ? "s" : ""}`}
                    </span>
                    <Button
                        value="Discard Changes"
                        onClick={discard}
                        disabled={saving}
                        classes={[styles.detailBtn]}
                        dataTestId="platform-discard"
                    />
                    <Button
                        value={saving ? "Saving..." : "Confirm Changes"}
                        onClick={confirm}
                        disabled={saving || termsIncomplete}
                        classes={[styles.removeBtn]}
                        dataTestId="platform-confirm"
                    />
                </div>
            )}
        </div>
    );
};

export default PlatformSettings;
